import {
    Column,
} from 'app/types/simpletable.types'
import getUniqueId from 'app/utils/getUniqueId'
import SkyNetLink from 'app/shared-components/SkyNetLink'
import {
    SizeLink, TypeLink,
} from 'app/shared-components/SkyNetLink/SkyNetLink.enum'
import InputSingleline from 'app/shared-components/InputSingleline'
import EnumSelector from 'app/shared-components/EnumSelector'
import MultipleProductTypeSelector from 'app/shared-components/MultipleProductTypeSelector'

import {
    AccountLane,
} from 'app/Apps/AccountManagement/AccountLanes/AccountLanes.types'
import {
    CollectionServiceType, DeliveryServiceType, ProductType,
} from 'app/enums/label.enums'
import Checkbox from 'app/shared-components/Checkbox'
import {
    isCollection, isDeliveryRoad,
} from 'app/Apps/Pricing/utils/utils'
import {
    RequestedLane,
} from 'app/Apps/AccountManagement/AQuotes/AQuotes.types'

export type QuotationOptionsTableType = AccountLane & RequestedLane & {
    baseLeaseDays?: string,
    productTypes?: any,
    opportunityId: number,
    selected?: boolean,
    otherComments?: string,
    requestedLaneId?: number,
}

const getColumns = ({
    classes,
    handleChange,
    l,
    getAllowedValues,
    createLaneLink,
    createOpportunityLink,
    disabled,
    selectAll,
}: {
    classes: Record<string, any>,
    l: (l: string) => string,
    handleChange: (...args: any[]) => void,
    createLaneLink: (id: number) => string,
    createOpportunityLink: (id: number) => string,
    getAllowedValues: (enumValue: Record<string, string>) => Record<string, string>,
    disabled?: boolean,
    selectAll?: JSX.Element
}): Column<keyof QuotationOptionsTableType>[] => {
    return [
        {
            label: selectAll,
            component: Checkbox,
            fieldName: 'selected',
            getProps: ({
                id,
                requestedLaneId,
                selected,
            }) => {
                return {
                    className: classes.checkbox,
                    disabled: disabled || Boolean(requestedLaneId),
                    onChange: (value: boolean) => {
                        handleChange({
                            id,
                            selected: value,
                        })
                    },
                    value: Boolean(requestedLaneId) || selected,
                }
            },
            width: 50,
            id: getUniqueId(),
        },
        {
            label: l('AccountLane.laneNumber'),
            component: SkyNetLink,
            fieldName: 'laneNumber',
            getProps: ({
                id, laneNumber,
            }) => {
                return {
                    size: SizeLink.S,
                    type: TypeLink.EXTERNAL,
                    text: laneNumber,
                    link: createLaneLink(id),
                    customClasses: {
                        wrapper: classes.link,
                    },
                }
            },
            id: getUniqueId(),
        },
        {
            label: l('AccountLane.opportunity'),
            component: SkyNetLink,
            fieldName: 'opportunity',
            getProps: ({
                opportunityId, opportunity,
            }) => {
                return {
                    size: SizeLink.S,
                    type: TypeLink.EXTERNAL,
                    text: opportunity,
                    link: createOpportunityLink(opportunityId),
                    customClasses: {
                        wrapper: classes.link,
                    },
                }
            },
            id: getUniqueId(),
        },
        {
            label: l('AccountQuotes.opportunityFrom'),
            component: InputSingleline,
            fieldName: 'originAirport',
            getProps: () => {
                return {
                    classNames: {
                        label: classes.inputLabel,
                    },
                    disabled: true,
                }
            },
            id: getUniqueId(),
        },
        {
            label: l('AccountQuotes.opportunityTo'),
            component: InputSingleline,
            fieldName: 'destinationAirport',
            getProps: () => {
                return {
                    classNames: {
                        label: classes.inputLabel,
                    },
                    disabled: true,
                }
            },
            id: getUniqueId(),
        },
        {
            label: l('AccountQuotes.laneHandover'),
            component: InputSingleline,
            fieldName: 'handoverPoint',
            getProps: ({
                deliveryServiceType,
                handoverPointZipCode,
                handoverPointCountryName,
            }) => {
                return {
                    classNames: {
                        label: classes.inputLabel,
                    },
                    disabled: true,
                    value: isDeliveryRoad(deliveryServiceType)
                        ? `${handoverPointZipCode} ${handoverPointCountryName}`
                        : '-',
                }
            },
            id: getUniqueId(),
        },
        {
            label: l('AccountQuotes.laneCollectionDropoff'),
            component: InputSingleline,
            fieldName: 'collectionDropoff',
            getProps: ({
                collectionServiceType,
                collectionDropoffPointZipCode,
                collectionDropoffPointCountryName,
            }) => {
                return {
                    classNames: {
                        label: classes.inputLabel,
                    },
                    disabled: true,
                    value: isCollection(collectionServiceType)
                        ? `${collectionDropoffPointZipCode} ${collectionDropoffPointCountryName}`
                        : '-',
                }
            },
            id: getUniqueId(),
        },
        {
            label: l('AccountQuotes.laneDeliveryType'),
            component: EnumSelector,
            fieldName: 'deliveryServiceType',
            getProps: () => {
                return {
                    className: classes.enumInput,
                    disabled: true,
                    allowedValues: getAllowedValues(DeliveryServiceType),
                }
            },
            id: getUniqueId(),
        },
        {
            label: l('AccountQuotes.laneCollectionType'),
            component: EnumSelector,
            fieldName: 'collectionServiceType',
            getProps: () => {
                return {
                    className: classes.enumInput,
                    disabled: true,
                    allowedValues: getAllowedValues(CollectionServiceType),
                }
            },
            id: getUniqueId(),
        },
        {
            label: l('AccountQuotes.productTypes'),
            component: MultipleProductTypeSelector,
            fieldName: 'productTypes',
            required: true,
            getProps: ({
                id,
                temperatureRange,
                requestedLaneId,
            }) => {
                return {
                    className: classes.productTypesInput,
                    temperatureRange,
                    disabled: disabled || Boolean(requestedLaneId),
                    required: true,
                    title: undefined,
                    onChange: (productTypes: ProductType[]) => {
                        handleChange({
                            id,
                            productTypes,
                        })
                    },
                }
            },
            width: 350,
            id: getUniqueId(),
        },
        {
            label: l('AccountQuotes.laneBaseLeaseDays'),
            component: InputSingleline,
            fieldName: 'baseLeaseDays',
            required: true,
            getProps: ({
                id,
                requestedLaneId,
                baseLeaseDays,
            }) => {
                return {
                    classNames: {
                        label: classes.inputLabel,
                    },
                    disabled: Boolean(requestedLaneId),
                    required: true,
                    placeholder: 'e.g. 1;2',
                    onChange: (value: string) => {
                        handleChange({
                            id,
                            baseLeaseDays: value,
                        })
                    },
                    value: Array.isArray(baseLeaseDays) ? baseLeaseDays.join('; ') : baseLeaseDays,
                }
            },
            id: getUniqueId(),
        },
        {
            label: l('AccountQuotes.quoteLaneComment'),
            component: InputSingleline,
            fieldName: 'otherComments',
            getProps: ({
                id,
                requestedLaneId,
            }) => {
                return {
                    classNames: {
                        label: classes.inputLabel,
                    },
                    onChange: (value: string) => {
                        handleChange({
                            id,
                            otherComments: value,
                        })
                    },
                    disabled: requestedLaneId,
                }
            },
            id: getUniqueId(),
        },
    ]
}

export default getColumns
